import { formatarDecimal, lerDecimal } from '@via-tax/contracts'
import { Entrada } from '@via-tax/ui'
import { useState } from 'react'

/**
 * Campo numérico pt-BR: o valor é inteiro em unidades mínimas (centavos, ppm) e aparece sempre
 * formatado ("1.234,56", "17,00"). Enquanto digita, o texto fica como está; ao sair, volta formatado.
 */
const TIPOS = {
  /** centavos → "1.234,56" */
  reais: { casasEscala: 2, casas: 2, casasMax: 2, prefixo: 'R$', sufixo: '' },
  /** ppm → "17,00" */
  percentual: { casasEscala: 4, casas: 2, casasMax: 2, prefixo: '', sufixo: '%' },
  /** fator em ppm (1.000.000 = 1) → "1,00" a "0,8765" */
  fator: { casasEscala: 6, casas: 2, casasMax: 4, prefixo: '', sufixo: '' },
} as const

export function EntradaNumero({
  valor,
  onValor,
  tipo,
  vazio,
  min,
  max,
  disabled,
  'aria-label': ariaLabel,
}: {
  valor: number | null
  onValor: (v: number | null) => void
  tipo: keyof typeof TIPOS
  /** texto mostrado quando o campo pode ficar vazio (valor null) */
  vazio?: string
  min?: number
  max?: number
  disabled?: boolean
  'aria-label'?: string
}) {
  const t = TIPOS[tipo]
  const formatado = valor === null ? '' : formatarDecimal(valor, t.casasEscala, t.casas, t.casasMax)
  const [editando, setEditando] = useState<string | null>(null)
  const texto = editando ?? formatado
  const lido = editando === null ? valor : lerDecimal(editando, t.casasEscala)
  const invalido = lido === undefined || (lido === null && vazio === undefined) || (typeof lido === 'number' && ((min !== undefined && lido < min) || (max !== undefined && lido > max)))
  const limitar = (v: number) => Math.min(max ?? v, Math.max(min ?? v, v))

  return (
    <div className="relative">
      {t.prefixo && <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-tinta-3">{t.prefixo}</span>}
      <Entrada
        inputMode="decimal"
        value={texto}
        placeholder={vazio ?? (tipo === 'fator' ? '1,00' : '0,00')}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-invalid={invalido || undefined}
        className={`num text-right ${t.prefixo ? 'pl-9' : ''} ${t.sufixo ? 'pr-8' : ''} ${invalido ? 'border-alerta focus:border-alerta' : ''}`}
        onFocus={(e) => {
          setEditando(formatado)
          e.currentTarget.select()
        }}
        onChange={(e) => {
          setEditando(e.target.value)
          const v = lerDecimal(e.target.value, t.casasEscala)
          if (v === null && vazio !== undefined) onValor(null)
          else if (typeof v === 'number') onValor(limitar(v))
        }}
        onBlur={() => {
          const v = editando === null ? undefined : lerDecimal(editando, t.casasEscala)
          if (v === null) onValor(vazio !== undefined ? null : 0)
          setEditando(null)
        }}
      />
      {t.sufixo && <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-tinta-3">{t.sufixo}</span>}
    </div>
  )
}
